import { z } from "zod";

export const STAGES = [
  // Parallel track for technician conversations (recruitment prospects and
  // active hired techs). Inbound messages from a B_TUSERS or B_TCOMPETITOR
  // phone/email land here instead of the customer pipeline, and Emma is
  // skipped (customer-flavoured drafts are wrong for tech conversations).
  // Listed FIRST so the Pipeline view opens with the Tech column on the
  // left — tech conversations are first-priority during the recruitment push.
  "tech",
  "lead",
  "opportunity",
  "chase",
  "schedule",
  "won",
  "lost",
] as const;
export type Stage = (typeof STAGES)[number];
export const StageSchema = z.enum(STAGES);

// Actor classification for inbound message routing. Driven by the matching
// path in ingestMessage (B_TUSERS / B_TCOMPETITOR before B_TCONTACT). Only
// `customer` triggers the Emma AI draft; the two tech kinds skip it.
export const ACTOR_KINDS = ["customer", "tech_active", "tech_prospect"] as const;
export type ActorKind = (typeof ACTOR_KINDS)[number];
export const ActorKindSchema = z.enum(ACTOR_KINDS);

export const CHANNELS = ["email", "sms", "whatsapp", "voip"] as const;
export type Channel = (typeof CHANNELS)[number];
export const ChannelSchema = z.enum(CHANNELS);

export const DIRECTIONS = ["in", "out"] as const;
export const DirectionSchema = z.enum(DIRECTIONS);

export const MESSAGE_STATUSES = [
  "received",
  "queued",
  "sent",
  "delivered",
  "failed",
] as const;
export const MessageStatusSchema = z.enum(MESSAGE_STATUSES);

export const CardSchema = z.object({
  cardId: z.number().int().positive(),
  vid: z.number().int().positive().nullable(),
  // Derived: vid if set, else newest visit on the linked account (by VID
  // desc). Computed server-side in getCardView so agent-facing consumers
  // (Emma / WF3) always operate on the freshest visit even when no human
  // has explicitly anchored card.vid yet. The QuoteEditor + human UI keep
  // using `vid` so card.vid = null still surfaces "no quote anchored on
  // this card yet" instead of silently jumping into an arbitrary history.
  // Falls back to null when vid is null AND there are no visits on the
  // account (true unmatched-lead orphans).
  effectiveVid: z.number().int().positive().nullable(),
  aid: z.number().int().positive(),
  cid: z.number().int().positive(),
  stage: StageSchema,
  position: z.number().int(),
  assignedUser: z.string().nullable(),
  headline: z.string().nullable(),
  priority: z.number().int().min(0).max(1),
  lastActivityAt: z.string().nullable(),
  closedReason: z.string().nullable(),
  archivedAt: z.string().nullable(),
  // Set Reminder snooze: ISO timestamp when the card should be brought back.
  // While non-null, the card is also archived. Cleared by the hourly sweep
  // (or by the user clicking "Clear reminder").
  reminderAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Card = z.infer<typeof CardSchema>;

// Pending AI action — a draft reply awaiting human approval on the card.
export const PendingActionSchema = z.object({
  type: z.enum(["draft_reply"]),
  source: z.string(),
  createdAt: z.string(),
  payload: z.object({
    channel: ChannelSchema,
    to: z.string(),
    subject: z.string().optional(),
    bodyText: z.string(),
    bodyHtml: z.string().optional(),
  }),
});
export type PendingAction = z.infer<typeof PendingActionSchema>;

// CardView: Card + hydrated display fields (contact name, postcode, scheduled date)
// Pulled from b_tcontact / b_tvisit / b_tlocation in the same query as cards.
export const CardViewSchema = CardSchema.extend({
  contactName: z.string().nullable(),
  // Breeze account name from B_TACCOUNT (null if the card has no linked aid).
  // UI prefers this over contactName as the headline — a card represents an
  // account cluster, not just the seed contact.
  accountName: z.string().nullable(),
  // True if the currently-displayed identity on this card (contactName, or
  // email/phone fallback) was populated by the AI extractor and hasn't been
  // overridden by a human.
  displayedIdentityFromAi: z.boolean(),
  postcode: z.string().nullable(),
  scheduleDate: z.string().nullable(),
  // Start time of the scheduled visit (HH:MM) and technician nickname
  // (from B_TUSERS.NICK_NAME joined on v.TECHNICIAN). Surfaced on WON
  // cards so the column doubles as a mini upcoming-schedule view.
  scheduleStartTime: z.string().nullable(),
  technicianNickname: z.string().nullable(),
  totalCost: z.number().nullable(),
  // Most recent message timestamp + direction (null = no messages yet).
  // Drives the SLA ticker on the card.
  lastMessageAt: z.string().nullable(),
  lastMessageDirection: DirectionSchema.nullable(),
  // Preview of the newest message on the card (subject + body + channel).
  // Rendered as two lines under the price on the board card.
  lastMessageChannel: ChannelSchema.nullable(),
  lastMessageSubject: z.string().nullable(),
  lastMessageBody: z.string().nullable(),
  // Agent attribution for the most recent outbound message on this card.
  // 'emma_auto' → steady sparkle (Emma handled it). 'emma_draft' → human
  // approved, no sparkle. null → human-composed or no outbound yet.
  lastOutboundAgent: z.enum(["emma_auto", "emma_draft"]).nullable(),
  // Team-shared "last time anyone viewed this card". Null if nobody ever
  // has. Client derives unread as lastMessageAt > lastReadAt. Mark-unread
  // clears it to resurface the card for the whole team.
  lastReadAt: z.string().nullable(),
  // Manual attention flag — set by Mark Unread, cleared by any staff read.
  // Covers cards with no inbound message where the time-based comparison
  // has nothing to beat.
  flaggedAt: z.string().nullable(),
  // AI-generated draft or action awaiting human approval.
  pendingAction: PendingActionSchema.nullable(),
  // Tech-stage links. Exactly one of these is non-null when stage='tech'
  // (or both null while a card is being seeded). Drives the PROSPECT /
  // ACTIVE badge on Card.tsx and tells the Slideout to hide
  // quote/visit affordances. Customer-pipeline cards always have both null.
  techUserId: z.number().int().positive().nullable(),
  techCompetitorId: z.number().int().positive().nullable(),
});
export type CardView = z.infer<typeof CardViewSchema>;

export const BoardResponseSchema = z.object({
  cards: z.array(CardViewSchema),
  generatedAt: z.string(),
});
export type BoardResponse = z.infer<typeof BoardResponseSchema>;

export const PatchCardSchema = z.object({
  stage: StageSchema.optional(),
  position: z.number().int().optional(),
  assignedUser: z.string().nullable().optional(),
  priority: z.number().int().min(0).max(1).optional(),
  closedReason: z.string().max(120).nullable().optional(),
  archive: z.boolean().optional(),
  // Set Reminder. Future ISO datetime → snooze: archive + store reminder.
  // null → clear reminder (and unarchive if it was reminder-archived).
  // Server rejects past timestamps with 400.
  reminderAt: z.string().datetime().nullable().optional(),
  originatorSessionId: z.string().optional(),
});

// Bulk card mutations — same patch shape applied to every id.
// Omit `stage` + `position` here because those only make sense per-card; bulk
// stage moves are supported but positions can't be meaningfully set together.
export const BulkPatchCardSchema = z.object({
  cardIds: z.array(z.number().int().positive()).min(1).max(200),
  patch: z.object({
    stage: StageSchema.optional(),
    assignedUser: z.string().nullable().optional(),
    priority: z.number().int().min(0).max(1).optional(),
    archive: z.boolean().optional(),
    closedReason: z.string().max(120).nullable().optional(),
  }),
});
export type BulkPatchCard = z.infer<typeof BulkPatchCardSchema>;
export type PatchCard = z.infer<typeof PatchCardSchema>;

export const AttachmentSchema = z.object({
  attachmentId: z.number().int().positive(),
  filename: z.string(),
  mimeType: z.string().nullable(),
  sizeBytes: z.number().int().nonnegative().nullable(),
  url: z.string(),
  // Vision-generated photo summary (set by the Inbound Vision Tagger n8n
  // workflow shortly after an inbound image attachment lands). Null until
  // processed; non-image attachments stay null permanently.
  description: z.string().nullable().default(null),
});
export type Attachment = z.infer<typeof AttachmentSchema>;

export const MessageSchema = z.object({
  msgId: z.number().int().positive(),
  channel: ChannelSchema,
  direction: DirectionSchema,
  cid: z.number().int().positive().nullable(),
  cardId: z.number().int().positive().nullable(),
  threadKey: z.string().nullable(),
  externalId: z.string().nullable(),
  fromAddress: z.string().nullable(),
  toAddress: z.string().nullable(),
  subject: z.string().nullable(),
  bodyText: z.string().nullable(),
  bodyHtml: z.string().nullable(),
  status: MessageStatusSchema,
  errorMessage: z.string().nullable(),
  sentBy: z.string().nullable(),
  // Outbound-only: identifies AI authorship. NULL = human typed + sent;
  // 'emma_auto' = Emma sent directly; 'emma_draft' = Emma drafted + a
  // human reviewed + hit Send. Used by the timeline label and the card
  // sparkle state ("last outbound from Emma → steady sparkle").
  agent: z.enum(["emma_auto", "emma_draft"]).nullable(),
  createdAt: z.string(),
  attachments: z.array(AttachmentSchema).default([]),
});
export type Message = z.infer<typeof MessageSchema>;

export const MessageListResponseSchema = z.object({
  messages: z.array(MessageSchema),
});
export type MessageListResponse = z.infer<typeof MessageListResponseSchema>;

// Card activity — stage changes, notes, message refs, system events,
// payment-ledger audit events.
export const ACTIVITY_TYPES = [
  "stage_change",
  "note",
  "message_ref",
  "system",
  "payment",
] as const;
export const ActivityTypeSchema = z.enum(ACTIVITY_TYPES);
export type ActivityType = (typeof ACTIVITY_TYPES)[number];

export const ActivitySchema = z.object({
  activityId: z.number().int().positive(),
  cardId: z.number().int().positive(),
  type: ActivityTypeSchema,
  fromStage: StageSchema.nullable(),
  toStage: StageSchema.nullable(),
  msgId: z.number().int().positive().nullable(),
  noteBody: z.string().nullable(),
  actor: z.string().nullable(),
  createdAt: z.string(),
});
export type Activity = z.infer<typeof ActivitySchema>;

export const ActivityListResponseSchema = z.object({
  activity: z.array(ActivitySchema),
});
export type ActivityListResponse = z.infer<typeof ActivityListResponseSchema>;

export const PostNoteSchema = z.object({
  note: z.string().min(1).max(4000),
});
export type PostNote = z.infer<typeof PostNoteSchema>;

// --- Quotes / orders (Stage 3 — port of quick-form.php) -------------------

export const QuoteItemSchema = z.object({
  chargeId: z.number().int().nullable(),
  chargemasterId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  quantity: z.number().int().nonnegative(),
  unitPrice: z.number(),
  isTaxable: z.boolean(),
  taxRate: z.number(),
  priceCodeId: z.number().int().nullable(),
  itemCondition: z.string().nullable(), // "Average" | "Good" | "Bad" | null
  discountType: z.enum(["Fixed Amount", "Percentage"]).nullable(),
  discountAmount: z.number(),
  discountPercent: z.number(),
  // Who absorbs the discount on this line. "breeze" = full discount comes
  // off Breeze's share; "split" = 50/50 between Breeze and the technician.
  // Stored implicitly via B_TCHARGE.TECHDISCOUNT/BREEZEDISCOUNT — UI default
  // is "breeze" and TECHDISCOUNT stays 0 for those rows.
  discountSplit: z.enum(["breeze", "split"]).default("breeze"),
  displayOrder: z.number().int(),
  estimatedMinutes: z.number().int().nullable(),
});
export type QuoteItem = z.infer<typeof QuoteItemSchema>;

export const QuoteContactSchema = z.object({
  cid: z.number().int().nullable(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  // `phone` is the legacy single-field input; new callers should use the
  // typed phone fields below. Kept for back-compat with existing consumers
  // and templated to the contact's mobile when present.
  phone: z.string(),
  phoneMobile: z.string().default(""),
  phoneHome: z.string().default(""),
  phoneWork: z.string().default(""),
  phoneOther: z.string().default(""),
});
export type QuoteContact = z.infer<typeof QuoteContactSchema>;

export const QuoteLocationSchema = z.object({
  lid: z.number().int().nullable(),
  address1: z.string(),
  address2: z.string(),
  address3: z.string(),
  city: z.string(),
  postcode: z.string(),
  // Property-level fields from B_TLOCATION — surfaced so the QuoteEditor
  // can edit them and the cleaner job page / work-order PDF can read
  // them. The first three drive layout decisions (Office vs House,
  // 3rd floor with no lift, etc); freetext propertyLevel preserves the
  // legacy mix of "1st" / "ground" / "1st and 2nd" — we don't normalise.
  propertyType: z.string(),
  propertyLevel: z.string(),
  liftAvailable: z.string(), // "yes" / "no" / ""
});
export type QuoteLocation = z.infer<typeof QuoteLocationSchema>;

export const QuoteTotalsSchema = z.object({
  subtotal: z.number(),
  tax: z.number(),
  discountTotal: z.number(),
  total: z.number(),
  // Display-only: if total is non-zero but below MIN_CHARGE, UI may show
  // this instead. Server does NOT enforce it on save.
  minimumCharge: z.number(),
});
export type QuoteTotals = z.infer<typeof QuoteTotalsSchema>;

export const QuoteSchema = z.object({
  vid: z.number().int().nullable(), // null for unsaved new
  visitId: z.string().nullable(),
  visitNumberText: z.string().nullable(),
  workOrderStatus: z.string().nullable(),
  accountId: z.string().nullable(),
  aid: z.number().int(),
  invoiceId: z.string().nullable(),
  scheduleDate: z.string().nullable(),
  startTime: z.string().nullable(),
  endTime: z.string().nullable(),
  contact: QuoteContactSchema,
  location: QuoteLocationSchema,
  // Account-level customer type from B_TACCOUNT.CUSTOMERTYPE.
  // "Residential" / "Commercial" (~30k Residential / 74 Commercial today).
  customerType: z.string(),
  // Visit-level parking arrangement from B_TVISIT.PARKING. Mirrors the
  // legacy order-form.php dropdown (Free parking on street, Driveway
  // parking, Meter parking customer will pay, etc). Empty string = not set.
  parking: z.string(),
  technicianId: z.string().nullable(),
  technicianName: z.string().nullable(),
  items: z.array(QuoteItemSchema),
  totals: QuoteTotalsSchema,
  // kanban_card.card_id for this visit (null if no card yet — brand-new quote
  // created server-side but not surfaced on the board). Drives the native
  // Notify modal so outbound messages log against the right card.
  cardId: z.number().int().positive().nullable(),
  // Billing contact + location (B_TINVOICE.BILLINGCONTACTID /
  // BILLINGLOCATIONID resolved back to numeric CID / LID). When these
  // match the service contact / location the QuoteEditor renders billing
  // = service; when they diverge the "Different billing" toggles are on
  // and the billing pickers show.
  billingContactId: z.number().int().nullable(),
  billingLocationId: z.number().int().nullable(),
  // Visit-level "apply this % to every line" panel state.
  //   null  = panel off → per-line discount controls work as today
  //   number = panel on → every line is forced to this percentage with
  //            Breeze-absorbs policy. QuoteEditor hides the per-line
  //            discount UI while this is on.
  // Initialised by create-stub when a customer arrives via /quote with a
  // discounted slot (preferredSlot.discountPct); staff can toggle off in
  // QuoteEditor. The customer-shown % is also preserved in B_TPRIVATENOTES
  // as an immutable audit note regardless of any later override.
  defaultDiscountPct: z.number().min(0).max(100).nullable(),
  // Payment summary off B_TINVOICE — drives the order-form Payments section
  // (Payment Type / Status / Invoice Total / Amount Paid / Outstanding) and
  // the Take Card / Cheque / Cash / BACS Received buttons. null = no invoice
  // attached to this visit yet (brand-new draft).
  payment: z
    .object({
      paymentTypeId: z.number().int().nullable(),
      paymentTypeLabel: z.string().nullable(),
      paymentStatusId: z.number().int().nullable(),
      paymentStatusLabel: z.string().nullable(),
      invoiceTotal: z.number(),
      amountPaid: z.number(),
      outstanding: z.number(),
    })
    .nullable(),
});
export type Quote = z.infer<typeof QuoteSchema>;

export const QuoteResponseSchema = z.object({
  quote: QuoteSchema,
});
export type QuoteResponse = z.infer<typeof QuoteResponseSchema>;

// Method tokens for POST /api/quotes/:vid/record-payment. Same set as the
// tech-portal payment route. card_paylink mints a paylink AND sets the
// invoice to "Paylink sent" — the route returns the URL so the staff
// member can open it / share it with the customer.
export const RecordVisitPaymentSchema = z.object({
  method: z.enum(["cash", "bacs", "cheque_tech", "cheque_customer", "card_paylink"]),
});
export type RecordVisitPayment = z.infer<typeof RecordVisitPaymentSchema>;

export const RecordVisitPaymentResponseSchema = z.object({
  ok: z.literal(true),
  paylinkUrl: z.string().url().nullable(),
});
export type RecordVisitPaymentResponse = z.infer<
  typeof RecordVisitPaymentResponseSchema
>;

// Catalog of payment types and the valid statuses within each, mirrored
// from B_TPAYMENTTYPE / B_TPAYMENTSTATUS. Drives the admin Payment Type +
// Payment Status selects on the QuoteEditor.
export const PaymentTypeCatalogEntrySchema = z.object({
  paymentTypeId: z.number().int(),
  paymentType: z.string(),
  statuses: z.array(
    z.object({
      paymentStatusId: z.number().int(),
      paymentStatus: z.string(),
    })
  ),
});
export const PaymentTypeCatalogResponseSchema = z.object({
  types: z.array(PaymentTypeCatalogEntrySchema),
});
export type PaymentTypeCatalogEntry = z.infer<
  typeof PaymentTypeCatalogEntrySchema
>;

// Admin direct-edit body for PUT /api/quotes/:vid/payment. Either field
// may be null to reset back to "Unknown". The route layer enforces admin
// gate + that the visit has an invoice attached.
export const SetVisitPaymentSchema = z.object({
  paymentTypeId: z.number().int().nullable(),
  paymentStatusId: z.number().int().nullable(),
});
export type SetVisitPayment = z.infer<typeof SetVisitPaymentSchema>;

// Save payload — atomic replace of all B_TCHARGE rows for the visit.
export const SaveQuoteItemSchema = z.object({
  chargemasterId: z.string().min(1),
  description: z.string(),
  quantity: z.number().int().positive(),
  unitPrice: z.number().nonnegative(),
  isTaxable: z.boolean(),
  taxRate: z.number().nonnegative(),
  priceCodeId: z.number().int().nullable(),
  itemCondition: z.string().nullable(),
  discountType: z.enum(["Fixed Amount", "Percentage"]),
  discountAmount: z.number().nonnegative(),
  discountPercent: z.number().min(0).max(100),
  // Defaults to "breeze" so existing call sites keep their current
  // behaviour (Breeze absorbs full discount, tech earnings unchanged).
  discountSplit: z.enum(["breeze", "split"]).default("breeze"),
});
export type SaveQuoteItem = z.infer<typeof SaveQuoteItemSchema>;

export const SaveQuoteContactSchema = z.object({
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  phone: z.string(),
  phoneMobile: z.string().optional(),
  phoneHome: z.string().optional(),
  phoneWork: z.string().optional(),
  phoneOther: z.string().optional(),
});
export type SaveQuoteContact = z.infer<typeof SaveQuoteContactSchema>;

export const SaveQuoteLocationSchema = z.object({
  address1: z.string(),
  address2: z.string(),
  address3: z.string(),
  city: z.string(),
  postcode: z.string(),
  propertyType: z.string().optional(),
  propertyLevel: z.string().optional(),
  liftAvailable: z.string().optional(), // "yes" / "no" / ""
});
export type SaveQuoteLocation = z.infer<typeof SaveQuoteLocationSchema>;

// Visit-level fields the QuoteEditor can change alongside items / contact /
// location. workOrderStatus drives the coloured badge in the VisitsTab;
// scheduleDate + startTime + endTime drive the scheduled-appointment line.
// All four are stamped on B_TVISIT verbatim.
export const SaveQuoteScheduleSchema = z.object({
  workOrderStatus: z.string(),
  scheduleDate: z.string().nullable(), // YYYY-MM-DD or null
  startTime: z.string(), // HH:MM or ""
  endTime: z.string(), // HH:MM or ""
  // B_TVISIT.TECHNICIAN is a varchar that stores the numeric B_TUSERS.ID as
  // a string. Empty string means unassigned. We pass it through as a
  // string to keep that quirk intact.
  technicianId: z.string(),
});
export type SaveQuoteSchedule = z.infer<typeof SaveQuoteScheduleSchema>;

// Technician for the QuoteEditor picker. Populated by GET /api/technicians.
export const TechnicianSchema = z.object({
  id: z.number().int(),
  nickName: z.string(),
  firstName: z.string(),
  shedMode: z.string(),
  commissionPlan: z.string(),
});
export type Technician = z.infer<typeof TechnicianSchema>;

// The canonical list of WORKORDERSTATUS values order-form.php uses. Adding
// a new status means extending this list + updating the VisitsTab badge
// colour mapping in Slideout.tsx.
export const WORK_ORDER_STATUSES = [
  "Booking not made (Qualified)",
  "Reviewing (Converted)",
  "Booking made (Converted)",
  "Technician required (Converted)",
  "Cancelled",
  "Abandoned",
] as const;
export type WorkOrderStatus = (typeof WORK_ORDER_STATUSES)[number];

export const SaveQuoteSchema = z.object({
  items: z.array(SaveQuoteItemSchema),
  contact: SaveQuoteContactSchema.optional(),
  location: SaveQuoteLocationSchema.optional(),
  schedule: SaveQuoteScheduleSchema.optional(),
  // Repoint the visit at a different account in the cluster. When set,
  // saveQuote resolves the numeric AID to the varchar ACCOUNTID hash and
  // UPDATEs B_TVISIT.ACCOUNTID. Keeps the visit's ACCOUNTID coherent with
  // its SERVICECONTACTID / SERVICELOCATIONID — order-form.php expects
  // all three to belong to the same account.
  accountAid: z.number().int().nullable().optional(),
  // Switch the visit's SERVICECONTACTID / SERVICELOCATIONID to point at a
  // different existing contact/location. Used by the "Use existing
  // contact / address" pickers in QuoteEditor. When set, the contact /
  // location patches above target the newly-pointed-at row.
  serviceContactId: z.number().int().nullable().optional(),
  serviceLocationId: z.number().int().nullable().optional(),
  // Billing pointers updated on B_TINVOICE.BILLINGCONTACTID /
  // BILLINGLOCATIONID. When unset the API leaves the invoice row alone;
  // when set it resolves CID/LID → CONTACTID/LOCATIONID hashes and
  // UPDATEs the invoice.
  billingContactId: z.number().int().nullable().optional(),
  billingLocationId: z.number().int().nullable().optional(),
  // QuoteEditor's "default discount %" panel state. null clears the
  // visit-level override (per-line UI takes over again); a number turns
  // the panel on. When non-null the client is responsible for making
  // every saved item's discountPercent match this value — the server
  // just persists DEFAULT_DISCOUNT_PCT on B_TVISIT for re-open. Omitted
  // → no change to the existing visit value.
  defaultDiscountPct: z.number().min(0).max(100).nullable().optional(),
  // Account-level B_TACCOUNT.CUSTOMERTYPE patch. Omitted → no change.
  customerType: z.string().optional(),
  // Visit-level B_TVISIT.PARKING patch. Omitted → no change.
  parking: z.string().optional(),
});
export type SaveQuote = z.infer<typeof SaveQuoteSchema>;

// Technician notes on a visit — backed by B_TTECHNOTES (HTMLNOTES +
// USER lookup against B_TUSERS). Mirrors the tech-order-form.php feature.
export const TechNoteSchema = z.object({
  id: z.number().int(),
  vid: z.number().int(),
  htmlNotes: z.string(),
  timestamp: z.string(), // ISO-8601 UTC
  authorName: z.string(),
  type: z.string().nullable(),
});
export type TechNote = z.infer<typeof TechNoteSchema>;

export const TechNoteListResponseSchema = z.object({
  notes: z.array(TechNoteSchema),
});
export type TechNoteListResponse = z.infer<typeof TechNoteListResponseSchema>;

export const AddTechNoteSchema = z.object({
  htmlNotes: z.string().trim().min(1),
});
export type AddTechNote = z.infer<typeof AddTechNoteSchema>;

// Campaign send — POST /api/campaigns/send body. Called by n8n daily
// outreach workflows (Emma personalised, review request, etc.). CID-scoped
// outbound that doesn't create a kanban card. Card surfaces naturally if
// the customer replies via /api/ingest.
export const CampaignSendSchema = z.object({
  campaignKey: z.string().min(1).max(100),
  cid: z.number().int().positive(),
  channel: z.enum(["email", "sms", "whatsapp"]),
  to: z.string().min(1),
  subject: z.string().optional(),
  bodyText: z.string().min(1),
  bodyHtml: z.string().optional(),
  sequenceName: z.string().optional(),
  sequenceStep: z.number().int().positive().optional(),
  sequenceTotalSteps: z.number().int().positive().optional(),
  segmentTag: z.string().optional(),
  templateName: z.string().optional(),
  templateVariables: z.record(z.unknown()).optional(),
  // Per-call overrides (defaults to 55/day + 90-day cooldown).
  cooldownDays: z.number().int().min(0).max(3650).optional(),
  dailyCap: z.number().int().min(1).max(5000).optional(),
  // WA Content template dispatch (passes through to WF-OUT).
  contentSid: z.string().optional(),
  contentVariables: z.record(z.string()).optional(),
});
export type CampaignSend = z.infer<typeof CampaignSendSchema>;

export const CampaignSendResponseSchema = z.discriminatedUnion("status", [
  z.object({
    status: z.literal("sent"),
    msgId: z.number().int(),
    logId: z.number().int(),
    cap: z.object({ limit: z.number(), usedLast24h: z.number() }),
  }),
  z.object({
    status: z.literal("skipped"),
    reason: z.enum([
      "dnc",
      "dnc_marketing",
      "pita",
      "recent_send",
      "invalid_cid",
      "invalid_address",
    ]),
    cap: z.object({ limit: z.number(), usedLast24h: z.number() }),
  }),
  z.object({
    status: z.literal("capped"),
    cap: z.object({ limit: z.number(), usedLast24h: z.number() }),
  }),
]);
export type CampaignSendResponse = z.infer<typeof CampaignSendResponseSchema>;

// Account dedupe search powering the top-bar "New Quote" modal. At
// least one of name/email/phone/postcode must be non-empty. Scored
// matches surfaced with a matchedFields array so the UI can highlight
// which field hit.
export const AccountSearchMatchFieldSchema = z.enum([
  "name",
  "email",
  "phone",
  "postcode",
]);
export type AccountSearchMatchField = z.infer<typeof AccountSearchMatchFieldSchema>;

export const AccountSearchResultSchema = z.object({
  aid: z.number().int(),
  primaryCid: z.number().int().nullable(),
  primaryLid: z.number().int().nullable(),
  contactName: z.string(),
  email: z.string(),
  phone: z.string(),
  address1: z.string(),
  city: z.string(),
  postcode: z.string(),
  lastVisitDate: z.string().nullable(),
  visitCount: z.number().int(),
  cardId: z.number().int().nullable(),
  score: z.number(),
  matchedFields: z.array(AccountSearchMatchFieldSchema),
  deleted: z.boolean(),
});
export type AccountSearchResult = z.infer<typeof AccountSearchResultSchema>;

export const AccountSearchResponseSchema = z.object({
  results: z.array(AccountSearchResultSchema),
});
export type AccountSearchResponse = z.infer<typeof AccountSearchResponseSchema>;

// Catalog: categories + items + protector pairings
export const CatalogItemSchema = z.object({
  chargemasterId: z.string(),
  name: z.string(),
  description: z.string().nullable(),
  price: z.number(),
  priceCodeId: z.number().int().nullable(),
  isTaxable: z.boolean(),
  taxRate: z.number(),
  estimatedMinutes: z.number().int().nullable(),
  isProtector: z.boolean(), // true if this is a protector upsell variant
});
export type CatalogItem = z.infer<typeof CatalogItemSchema>;

export const CatalogCategorySchema = z.object({
  id: z.number().int(),
  name: z.string(),
  displayOrder: z.number().int(),
  items: z.array(CatalogItemSchema),
});
export type CatalogCategory = z.infer<typeof CatalogCategorySchema>;

export const CatalogResponseSchema = z.object({
  categories: z.array(CatalogCategorySchema),
  // regular chargemasterId → protector chargemasterId
  protectorMap: z.record(z.string(), z.string()),
});
export type CatalogResponse = z.infer<typeof CatalogResponseSchema>;

// Account lookups (contact + location pickers)
export const AccountContactSchema = z.object({
  cid: z.number().int(),
  fullName: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  phoneMobile: z.string(),
  phoneHome: z.string(),
  phoneWork: z.string(),
  phoneOther: z.string().default(""),
});
export type AccountContact = z.infer<typeof AccountContactSchema>;

export const AccountLocationSchema = z.object({
  lid: z.number().int(),
  address1: z.string(),
  address2: z.string(),
  address3: z.string().default(""),
  city: z.string(),
  postcode: z.string(),
  // Property metadata so the QuoteEditor's location picker can repopulate
  // the Property section when the user swaps to a different address on
  // the account — otherwise stale values from the previous location
  // get re-saved against the new one.
  propertyType: z.string().default(""),
  propertyLevel: z.string().default(""),
  liftAvailable: z.string().default(""), // "yes" | "no" | ""
});
export type AccountLocation = z.infer<typeof AccountLocationSchema>;

// --- Card cluster (duplicate-matched accounts/contacts/visits) -------------
// Mirrors the lookup logic in trengo/trengo3.php: a single kanban card
// actually represents a cluster of Breeze accounts linked by any contact's
// email or last-10-digits-of-phone match. The UI aggregates contacts,
// addresses, visits, and messages across the whole cluster.

export const ClusterAccountSchema = z.object({
  aid: z.number().int(),
  accountName: z.string(),
  accountNumber: z.string(),
  pita: z.boolean(),
  dnc: z.boolean(),
  dncMarketing: z.boolean(),
});
export type ClusterAccount = z.infer<typeof ClusterAccountSchema>;

export const ClusterContactSchema = z.object({
  cid: z.number().int(),
  aid: z.number().int(),
  accountName: z.string(),
  fullName: z.string(),
  firstName: z.string(),
  lastName: z.string(),
  email: z.string(),
  phoneMain: z.string(),
  phoneMobile: z.string(),
  phoneHome: z.string(),
  phoneWork: z.string(),
  phoneOther: z.string(),
});
export type ClusterContact = z.infer<typeof ClusterContactSchema>;

export const ClusterAddressSchema = z.object({
  lid: z.number().int(),
  aid: z.number().int(),
  accountName: z.string(),
  accountNumber: z.string(),
  address1: z.string(),
  address2: z.string(),
  address3: z.string().default(""),
  city: z.string(),
  postcode: z.string(),
  propertyType: z.string(),
});
export type ClusterAddress = z.infer<typeof ClusterAddressSchema>;

export const ClusterVisitSchema = z.object({
  vid: z.number().int(),
  visitNumberText: z.string(),
  workOrderStatus: z.string(),
  scheduleDate: z.string().nullable(), // YYYY-MM-DD or null
  startTime: z.string(),
  discussedDatesTimes: z.string(),
  dateCreated: z.string().nullable(), // ISO or null
  dateLastEdited: z.string().nullable(),
  postcode: z.string(),
  totalCost: z.number(),
  paidAmount: z.number(),
  outstanding: z.number(), // totalCost - paidAmount, clamped ≥ 0
  aid: z.number().int(),
  accountName: z.string(),
  accountNumber: z.string(),
  technicianName: z.string(),
});
export type ClusterVisit = z.infer<typeof ClusterVisitSchema>;

export const CardClusterSchema = z.object({
  // Null when the cluster was loaded by aid (View Account preview)
  // rather than from an existing kanban card.
  cardId: z.number().int().nullable(),
  seedCid: z.number().int().nullable(),
  primaryVid: z.number().int().nullable(),
  matchedCids: z.array(z.number().int()),
  matchedAids: z.array(z.number().int()),
  accounts: z.array(ClusterAccountSchema),
  contacts: z.array(ClusterContactSchema),
  addresses: z.array(ClusterAddressSchema),
  visits: z.array(ClusterVisitSchema),
  outstandingCount: z.number().int(),
});
export type CardCluster = z.infer<typeof CardClusterSchema>;

export const CardClusterResponseSchema = z.object({ cluster: CardClusterSchema });
export type CardClusterResponse = z.infer<typeof CardClusterResponseSchema>;

// --- AI contact extraction -------------------------------------------------
// Fields we support extracting. Narrow set for now; easy to extend.
export const CONTACT_FIELDS = [
  "firstName",
  "lastName",
  "email",
  "phoneMobile",
  "phoneMain",
] as const;
export type ContactField = (typeof CONTACT_FIELDS)[number];

export const ContactAiExtractSchema = z.object({
  source: z.string().min(1),
  msgId: z.number().int().positive().nullable().optional(),
  confidence: z.number().min(0).max(1).optional(),
  fields: z.object({
    firstName: z.string().nullable().optional(),
    lastName: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    phoneMobile: z.string().nullable().optional(),
    phoneMain: z.string().nullable().optional(),
  }),
});
export type ContactAiExtract = z.infer<typeof ContactAiExtractSchema>;

export const ContactAnnotationSchema = z.object({
  fieldName: z.string(),
  valueAfter: z.string(),
  source: z.string(),
  msgId: z.number().int().positive().nullable(),
  confidence: z.number().nullable(),
  createdAt: z.string(),
});
export type ContactAnnotation = z.infer<typeof ContactAnnotationSchema>;

export const ContactAnnotationsResponseSchema = z.object({
  annotations: z.array(ContactAnnotationSchema),
});
export type ContactAnnotationsResponse = z.infer<typeof ContactAnnotationsResponseSchema>;

export const IngestMessageSchema = z.object({
  channel: ChannelSchema,
  // voip-out uses this path too (recording ingest post-hoc). Email / SMS /
  // WhatsApp outbound still go through /api/messages/send + WF-OUT.
  direction: z.enum(["in", "out"]).default("in"),
  externalId: z.string().min(1),
  threadKey: z.string().nullable().optional(),
  fromAddress: z.string().min(1),
  toAddress: z.string().nullable().optional(),
  subject: z.string().nullable().optional(),
  bodyText: z.string().nullable().optional(),
  bodyHtml: z.string().nullable().optional(),
  rawPayload: z.unknown().optional(),
  // Structured sender profile passed by the producer (currently WF7-Bridge for
  // website contact form). When present AND no existing B_TCONTACT matches the
  // fromAddress, ingest will auto-create B_TACCOUNT + B_TCONTACT so the new
  // lead lands on a properly-linked card instead of as an orphan (cid=0/aid=0).
  senderProfile: z
    .object({
      firstName: z.string().optional(),
      lastName: z.string().optional(),
      email: z.string().optional(),
      phone: z.string().optional(),
      postcode: z.string().optional(),
    })
    .optional(),
  // Producer-driven "this inbound is a fresh lead — also create a B_TVISIT
  // stub" flag. Triggered today by WF7-Bridge (website contact form) and
  // the Next.js /api/quote/callback path on carpetcleaning.co.uk. The
  // WhatsApp /api/ingest path doesn't need this — it auto-detects the
  // website-origin signature in the body text via
  // lib/websiteOriginSignature.ts. Anything that arrives via a structured
  // producer (form, callback) sets this explicitly so we don't have to
  // parse body text we wrote ourselves.
  autoCreateStub: z.boolean().optional(),
  // Optional customer-preferred slot to drop onto the new B_TVISIT.
  // Source: the slot picker on /quote (sent through by the callback path)
  // or the human input on the contact form. Ignored if autoCreateStub is
  // false / absent.
  preferredSlot: z
    .object({
      date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
      ampm: z.enum(["AM", "PM"]),
      discountPct: z.number().int().min(0).max(100).optional().nullable(),
    })
    .nullable()
    .optional(),
  // Producer-supplied lead source tag — drives the audit-note text and
  // the analytics breakdown. Defaults to a value derived from the channel
  // when omitted.
  leadSource: z
    .enum(["website_form", "callback", "whatsapp_quote", "whatsapp_organic", "manual"])
    .optional(),
  attachments: z
    .array(
      z
        .object({
          filename: z.string(),
          mimeType: z.string().nullable().optional(),
          sizeBytes: z.number().int().nullable().optional(),
          // Inline base64 payload (used for Gmail — n8n downloads and embeds).
          contentBase64: z.string().optional(),
          // Remote URL the API will fetch server-side (used for Twilio MMS —
          // n8n passes the MediaUrl{N} and we download with Basic Auth).
          fetchUrl: z.string().url().optional(),
          fetchAuth: z.enum(["twilio"]).optional(),
        })
        .refine(
          (a) => Boolean(a.contentBase64) || Boolean(a.fetchUrl),
          { message: "attachment requires contentBase64 or fetchUrl" }
        )
    )
    .optional(),
  // Producer override for actor classification. WF-OUT sets this to
  // 'tech_prospect' when Andzelika sends an outreach from the Recruitment
  // dashboard so the resulting card lands in the Tech column from
  // message 1, without us having to wait for an inbound reply to detect
  // them via phone/email. Server still validates the FK exists.
  actorKind: ActorKindSchema.optional(),
  techCompetitorId: z.number().int().positive().optional(),
  techUserId: z.number().int().positive().optional(),
});
export type IngestMessage = z.infer<typeof IngestMessageSchema>;

export const SendMessageSchema = z.object({
  cardId: z.number().int().positive(),
  channel: ChannelSchema,
  to: z.string().min(1),
  subject: z.string().optional(),
  bodyText: z.string().min(1),
  bodyHtml: z.string().optional(),
  // IDs of rows in message_attachment created via /api/messages/upload-attachment.
  // The send handler re-links those rows to the new message row in one UPDATE.
  attachmentIds: z.array(z.number().int().positive()).optional(),
  // Emma attribution. Set by the n8n Emma workflow for automated sends
  // ('emma_auto') or by the pending-action accept handler ('emma_draft').
  // Human-composed sends leave this unset.
  agent: z.enum(["emma_auto", "emma_draft"]).optional(),
  // WhatsApp Content-template dispatch. When both are set, WF-OUT sends
  // via Twilio Content API (ContentSid + ContentVariables JSON) instead of
  // freeform Body — works outside the 24h session window.
  contentSid: z.string().min(1).optional(),
  contentVariables: z.record(z.string()).optional(),
  // When the FE sends a paylink-bearing template, it forwards the B_TPAYLINK.ID
  // it received from /notify-preview so the send handler can UPDATE
  // B_TPAYLINK.DATA with the recipient JSON (matches legacy send_pay_link).
  paylinkLinkId: z.number().int().positive().optional(),
});
export type SendMessage = z.infer<typeof SendMessageSchema>;

// Notify-modal templates ported from trengo_order_notify.php. Server renders
// the subject/body from B_TVISIT data so the UI only hands back a key.
export const NotifyTemplateKeySchema = z.enum([
  "customer_email_quote",
  "customer_email_quote-updated",
  "customer_email_confirmation",
  "customer_email_invoice",
  "customer_email_updated-order",
  "customer_email_ask_for_payment",
  "customer_sms_quote",
  "customer_sms_quote-updated",
  "customer_sms_confirmation",
  "customer_sms_ask_for_payment",
  // WhatsApp — one key per Twilio Content template SID.
  "customer_wa_appointment_reminder",
  "customer_wa_ask_cleaning_postcode",
  "customer_wa_ask_cleaning",
  "customer_wa_quote",
  "customer_wa_booking_confirmed",
  "customer_wa_ask_for_payment",
  "customer_wa_ask_for_photo",
  "customer_wa_call_permission",
  "customer_wa_ask_for_review",
  "customer_wa_6_month_refresh",
  "customer_wa_oops_pet_owners",
  "customer_wa_christmas_prep",
  "customer_wa_spring_special",
  "customer_wa_spring_allergy_relief",
  "customer_wa_how_can_i_help",
  "technician_email_work-order",
  "technician_sms_work-order",
]);
export type NotifyTemplateKey = z.infer<typeof NotifyTemplateKeySchema>;

export const NotifyTemplateMetaSchema = z.object({
  key: NotifyTemplateKeySchema,
  label: z.string(),
  channel: z.enum(["email", "sms", "whatsapp"]),
  group: z.enum(["Customer Email", "Customer SMS", "WhatsApp", "Technician"]),
});
export type NotifyTemplateMeta = z.infer<typeof NotifyTemplateMetaSchema>;

export const NotifyPreviewSchema = z.object({
  templateKey: NotifyTemplateKeySchema,
  channel: z.enum(["email", "sms", "whatsapp"]),
  to: z.string(),
  subject: z.string().nullable(),
  bodyText: z.string(),
  firstName: z.string(),
  visitNumberText: z.string(),
  // Populated for WhatsApp Content-template sends. If the UI forwards these
  // on send, WF-OUT dispatches via Twilio Content API (works outside the 24h
  // window). If staff edits the body the UI should drop these on send so it
  // falls back to freeform.
  contentSid: z.string().nullable(),
  contentVariables: z.record(z.string()).nullable(),
  // Populated only for templates in TEMPLATE_KEYS_WITH_PAYLINK (e.g.
  // customer_wa_ask_for_payment). The kanban minted a B_TPAYLINK row at
  // preview time and embedded its pay.php URL into bodyText / contentVariables.
  // The FE should forward paylinkLinkId on send so the API can UPDATE
  // B_TPAYLINK.DATA with recipient info.
  paylinkLinkId: z.number().int().positive().nullable(),
  paylinkUrl: z.string().nullable(),
});
export type NotifyPreview = z.infer<typeof NotifyPreviewSchema>;

export const PresenceViewerSchema = z.object({
  email: z.string(),
  color: z.string(),
});
export type PresenceViewer = z.infer<typeof PresenceViewerSchema>;

export const SSEEventSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal("card.created"), card: CardViewSchema }),
  z.object({
    type: z.literal("card.moved"),
    cardId: z.number().int().positive(),
    fromStage: StageSchema,
    toStage: StageSchema,
    position: z.number().int(),
    originatorSessionId: z.string().optional(),
  }),
  z.object({ type: z.literal("card.updated"), card: CardViewSchema }),
  z.object({ type: z.literal("card.activity"), activity: ActivitySchema }),
  z.object({
    type: z.literal("card.read_state"),
    cardId: z.number().int().positive(),
    // Team-shared timestamp. Clients replace the card's lastReadAt with this
    // value — read-state is shared, so every client reflects the same change.
    lastReadAt: z.string().nullable(),
    // Manual attention flag — mirrored from kanban_card.flagged_at after
    // the read/unread mutation. null after Mark Read, an ISO timestamp
    // after Mark Unread.
    flaggedAt: z.string().nullable(),
  }),
  z.object({
    type: z.literal("card.presence"),
    cardId: z.number().int().positive(),
    viewers: z.array(PresenceViewerSchema),
  }),
  z.object({ type: z.literal("message.received"), message: MessageSchema }),
  // Fires when an existing message gains attachments (async MMS fetch) or
  // any other field changes. Client replaces-in-place by msgId.
  z.object({ type: z.literal("message.updated"), message: MessageSchema }),
  z.object({
    type: z.literal("message.status_updated"),
    msgId: z.number().int().positive(),
    status: MessageStatusSchema,
    externalId: z.string().nullable(),
    errorMessage: z.string().nullable(),
  }),
  // Fires when a technician's idle state flips. Drives the in-app idle
  // banner refresh and (Phase 3) browser notifications. Emitted by
  // POST /api/coverage/evaluate-idle when state crosses the threshold.
  z.object({
    type: z.literal("technician.idle_changed"),
    technicianId: z.number().int().positive(),
    nickname: z.string(),
    isIdle: z.boolean(),
    sinceDays: z.number().int().nullable(),
  }),
]);
export type SSEEvent = z.infer<typeof SSEEventSchema>;

export const SessionUserSchema = z.object({
  email: z.string().email(),
  name: z.string().nullable(),
  picture: z.string().nullable(),
});
export type SessionUser = z.infer<typeof SessionUserSchema>;

export const STAGE_LABELS: Record<Stage, string> = {
  lead: "Lead",
  opportunity: "Opportunity",
  chase: "Chase",
  schedule: "Schedule",
  won: "Won",
  lost: "Lost",
  tech: "Technicians",
};

// ===========================================================================
// Cleaner Earnings Dashboard
// ===========================================================================
// Replaces the manual Excel-per-cleaner monthly pay process. The dashboard
// computes a statement for one technician over one period, splitting jobs
// into TWO buckets:
//   - cardBacsCheque  — Breeze received (or expects) the money. Owes cleaner the TECH_FEE.
//   - cash            — Cleaner kept the customer's cash. Owes Breeze the BREEZE_FEE.
//
// The cleaner gets paid as soon as the job is complete — even if the customer
// hasn't fully paid Breeze yet. Awaiting-payment is therefore NOT a bucket;
// it's a per-job flag (`awaitingCustomerPayment`) so the office can see which
// customer invoices to chase. The on-screen view shows a separate "Awaiting
// customer payment" list as a duplicate of those flagged rows; the printed
// PDF hides that list (cleaners don't need it).
//
// Cheque jobs default to the cardBacsCheque bucket. The CHEQUE_TREATED_AS_CASH
// per-tech flag flips them into the cash bucket — used by techs who take
// card on their own machine and record the visit as Cheque in the legacy
// tech-order-form.php (real cheques are basically extinct now).

export const STATEMENT_BUCKETS = ["cardBacsCheque", "cash"] as const;
export type StatementBucket = (typeof STATEMENT_BUCKETS)[number];
export const StatementBucketSchema = z.enum(STATEMENT_BUCKETS);

export const StatementJobSchema = z.object({
  vid: z.number().int().positive(),
  visitNumber: z.string(),
  scheduleDate: z.string(),                         // ISO date YYYY-MM-DD
  serviceAddressLine1: z.string().nullable(),
  postcode: z.string().nullable(),
  accountName: z.string().nullable(),
  techFee: z.number(),                              // £ (always positive)
  breezeFee: z.number(),                            // £
  totalCost: z.number(),                            // TOTALCOSTOFCHARGE
  totalPaid: z.number(),                            // TOTALPAYMENTMADE
  // Raw payment type from B_TPAYMENTTYPE.PAYMENTTYPE — Cash, Card, BACS,
  // Cheque, or NULL if no invoice / no payment type recorded yet.
  paymentType: z.string().nullable(),
  bucket: StatementBucketSchema,
  // Effective bucket if the cheque-as-cash flag had been off / on. Lets the
  // UI explain *why* a Cheque ended up in the cash column for the toggled tech.
  defaultBucket: StatementBucketSchema,
  // True when TOTALPAYMENTMADE < TOTALCOSTOFCHARGE on a non-cash job. Cash
  // jobs are never awaiting (cleaner already collected). The cleaner is paid
  // either way — this is a chase-the-customer signal, not a withholding gate.
  awaitingCustomerPayment: z.boolean(),
  manualTechPaid: z.boolean(),
  // £ already routed to the tech's Stripe Connect balance for this VID, summed
  // across B_TSTRIPE_JOB_PAYMENT rows excluding 'pending' and 'refunded'.
  // 0 when Flow A didn't fire (cash/BACS/cheque, or SAMEDAY_PAYMENT_ENABLED
  // was off at customer-payment time). Subtracted from techFee when computing
  // what Breeze still owes the cleaner — prevents double-paying on the
  // monthly statement. Optional with default 0 so historical closed-statement
  // snapshots (predating Flow A) deserialise cleanly.
  stripeAlreadyPaid: z.number().default(0),
});
export type StatementJob = z.infer<typeof StatementJobSchema>;

export const StatementSchema = z.object({
  technicianId: z.number().int().positive(),
  technicianName: z.string(),
  technicianCountry: z.string().nullable(),         // 'GB' | 'US' | null
  currencySymbol: z.string(),                       // '£' or '$'
  chequeTreatedAsCash: z.boolean(),
  // Reflects the tech's current SAMEDAY_PAYMENT_ENABLED flag — not a
  // historical snapshot, so closed statements show the *current* setting.
  // Drives the same-day toggle in the StripeConnectBar.
  samedayPaymentEnabled: z.boolean(),
  periodStart: z.string(),                          // YYYY-MM-DD
  periodEnd: z.string(),
  status: z.enum(["draft", "closed"]),
  openingBalance: z.number(),
  cardBacsChequeTotal: z.number(),                  // sum TECH_FEE (incl. awaiting) — GROSS
  cashTotal: z.number(),                            // sum BREEZE_FEE
  // Sum of stripeAlreadyPaid across cardBacsChequeJobs in £. Subtracted from
  // cardBacsChequeTotal when computing netDueToCleaner so the dashboard
  // doesn't ask the admin to BACS money the tech already received via Flow A
  // same-day pay. Always 0 for historical statements (Flow A is post-cutover).
  samedayPaidTotal: z.number().default(0),
  samedayPaidJobCount: z.number().int().nonnegative().default(0),
  netDueToCleaner: z.number(),                      // opening + (cardBacsCheque - samedayPaid) - cash
  paymentAmount: z.number().nullable(),
  paymentSentDate: z.string().nullable(),
  balanceCarriedForward: z.number().nullable(),
  notes: z.string().nullable(),
  // Job lists. For closed statements these are read from the frozen snapshot;
  // for drafts they are computed live from B_TVISIT. awaitingCustomerPaymentJobs
  // is a SUBSET of cardBacsChequeJobs (cash never awaiting) surfaced for
  // on-screen visibility — totals are not affected by it.
  cardBacsChequeJobs: z.array(StatementJobSchema),
  cashJobs: z.array(StatementJobSchema),
  awaitingCustomerPaymentJobs: z.array(StatementJobSchema),
  generatedAt: z.string(),
});
export type Statement = z.infer<typeof StatementSchema>;

export const TechnicianBalanceSchema = z.object({
  technicianId: z.number().int().positive(),
  nickname: z.string(),
  countryIso: z.string().nullable(),
  currencySymbol: z.string(),
  chequeTreatedAsCash: z.boolean(),
  // When true, paylinks for this tech's jobs split funds at customer-
  // payment time via Stripe Connect destination charges (Flow A in
  // project_stripe_connect_architecture memory). When false, all funds
  // land in Breeze and settle via the monthly Cleaner Earnings statement.
  samedayPaymentEnabled: z.boolean(),
  // Most recent closed statement's balance_carried_forward (or opening_balance
  // if no statements yet). Positive = Breeze owes cleaner; negative = cleaner
  // owes Breeze. Zero = settled.
  currentBalance: z.number(),
  lastPaymentSentDate: z.string().nullable(),
  lastClosedPeriodEnd: z.string().nullable(),
});
export type TechnicianBalance = z.infer<typeof TechnicianBalanceSchema>;

// POST /api/earnings/technicians/:id/payment body
export const RecordPaymentSchema = z.object({
  periodStart: z.string(),
  periodEnd: z.string(),
  paymentAmount: z.number(),                        // amount actually transferred
  paymentSentDate: z.string(),                      // YYYY-MM-DD
  notes: z.string().nullable().optional(),
  // How the payment is executed:
  //   'manual'  → admin just records the figure (existing behaviour;
  //               cheque, BACS, cash-in-hand). Default to preserve legacy.
  //   'stripe'  → fire a Stripe Connect transfer (platform → connected
  //               account) followed by an Instant Payout (connected
  //               account → tech bank, 1.5% Stripe fee absorbed by Breeze).
  //               Only valid if the tech has completed Stripe Connect
  //               onboarding with payouts_enabled = true.
  paymentMethod: z.enum(["manual", "stripe"]).default("manual"),
});
export type RecordPayment = z.infer<typeof RecordPaymentSchema>;

// PUT /api/earnings/technicians/:id/opening-balance body
export const SetOpeningBalanceSchema = z.object({
  asOfDate: z.string(),                             // day before first real period
  balance: z.number(),                              // positive = Breeze owes them
  notes: z.string().nullable().optional(),
});
export type SetOpeningBalance = z.infer<typeof SetOpeningBalanceSchema>;

// PATCH /api/earnings/technicians/:id body. All fields optional so the
// caller can update one flag at a time without resetting the others.
export const PatchTechnicianSchema = z.object({
  chequeTreatedAsCash: z.boolean().optional(),
  samedayPaymentEnabled: z.boolean().optional(),
});
export type PatchTechnician = z.infer<typeof PatchTechnicianSchema>;

// ---------------------------------------------------------------------------
// Per-job breakdown — line items + Stripe Connect payment audit. Loaded
// on-demand when admin expands a job row in the statement view. Lives at
// GET /api/earnings/jobs/:vid/breakdown.
// ---------------------------------------------------------------------------

export const JobLineItemSchema = z.object({
  chargeId: z.number().int(),
  displayOrder: z.number().int().nullable(),
  description: z.string(),
  quantity: z.number(),
  unitSellPrice: z.number(),
  subtotal: z.number(),
  discountAmount: z.number(),
  techEarn: z.number(),            // tech's share of this line (from B_TCHARGE.TECHEARN)
  breezeEarn: z.number(),          // Breeze's share of this line (from B_TCHARGE.BREEZEEARN)
  techDiscount: z.number(),        // £ of the line discount the tech absorbed (B_TCHARGE.TECHDISCOUNT)
  breezeDiscount: z.number(),      // £ of the line discount Breeze absorbed (B_TCHARGE.BREEZEDISCOUNT)
  // "split" = tech took half the discount on this line, "breeze" = Breeze
  // absorbed the whole £ off. Derived from TECHDISCOUNT > 0; mirrors the
  // implicit shape in quotes.ts mapCharge.
  discountAbsorber: z.enum(["split", "breeze"]),
  tax: z.number(),
});
export type JobLineItem = z.infer<typeof JobLineItemSchema>;

export const JobStripePaymentSchema = z.object({
  id: z.number().int(),
  paymentIntentId: z.string().nullable(),
  checkoutSessionId: z.string().nullable(),
  chargeId: z.string().nullable(),
  transferId: z.string().nullable(),
  payoutId: z.string().nullable(),
  amountTotal: z.number(),                      // £ customer paid
  applicationFeeAmount: z.number(),             // £ Breeze kept (gross)
  stripeFeeAmount: z.number().nullable(),        // £ Stripe took from Breeze
  techAmount: z.number(),                        // £ tech received
  status: z.enum([
    "pending",
    "paid",
    "payout_pending",
    "payout_paid",
    "payout_failed",
    "refunded",
  ]),
  failureMessage: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type JobStripePayment = z.infer<typeof JobStripePaymentSchema>;

export const JobBreakdownSchema = z.object({
  vid: z.number().int().positive(),
  visitNumber: z.string().nullable(),
  scheduleDate: z.string(),
  serviceAddressLine1: z.string().nullable(),
  postcode: z.string().nullable(),
  accountName: z.string().nullable(),
  paymentType: z.string().nullable(),
  // Visit-level totals from B_TVISIT.
  subtotal: z.number(),
  discountTotal: z.number(),
  taxTotal: z.number(),
  totalCost: z.number(),
  totalPaid: z.number(),
  techFee: z.number(),
  breezeFee: z.number(),
  currencySymbol: z.string(),
  lineItems: z.array(JobLineItemSchema),
  // Zero-or-more Stripe Connect payments tied to this job. Could be
  // empty (no online payment yet) or one+ (paylink paid, or paylink
  // paid then partially refunded — though refunds aren't in v1).
  stripePayments: z.array(JobStripePaymentSchema),
});
export type JobBreakdown = z.infer<typeof JobBreakdownSchema>;

// ===========================================================================
// Business Growth Dashboard
// ===========================================================================
// Read-only income view (Phase 1) + writeable expense tracking (Phase 2) +
// combined P&L (Phase 3). Data sources:
//   - Income comes from B_TVISIT (already queried for Cleaner Earnings).
//   - Conversion funnel reads card_activity stage transitions.
//   - Expenses live in two new tables: B_TEXPENSE_CATEGORY (user-managed
//     list) + B_TEXPENSE (manual recurring entries + bank-CSV imports).
//   - Cleaner pay rolls up automatically from B_TCLEANER_STATEMENT into the
//     "Wages" P&L line — no manual entry needed.

// ---- Phase 1: Income ----

export const HeadlineMetricSchema = z.object({
  current: z.number(),
  prevMonth: z.number(),
  prevYear: z.number(),
  // Percent deltas (multiplied by 100). null when divisor is 0 — caller
  // formats as "n/a" rather than a misleading "+∞%".
  prevMonthDeltaPct: z.number().nullable(),
  prevYearDeltaPct: z.number().nullable(),
});
export type HeadlineMetric = z.infer<typeof HeadlineMetricSchema>;

export const HeadlineSchema = z.object({
  month: z.string(),                                   // YYYY-MM
  revenue: HeadlineMetricSchema,                       // sum TOTALCOSTOFCHARGE on converted+published visits
  jobCount: HeadlineMetricSchema,
  averageJobValue: HeadlineMetricSchema,
  // lead→won conversion rate as a fraction 0..1 (UI shows × 100). Counts
  // distinct cards that ever entered 'lead' vs ever reached 'won' in month.
  conversionRate: HeadlineMetricSchema,
});
export type Headline = z.infer<typeof HeadlineSchema>;

export const RevenueTrendPointSchema = z.object({
  month: z.string(),                                   // YYYY-MM
  revenue: z.number(),
  jobCount: z.number(),
  avgJobValue: z.number(),
});
export type RevenueTrendPoint = z.infer<typeof RevenueTrendPointSchema>;

export const FunnelStageSchema = z.object({
  stage: StageSchema,
  count: z.number(),                                   // distinct cards that ever reached this stage in window
});
export type FunnelStage = z.infer<typeof FunnelStageSchema>;

export const ConversionFunnelSchema = z.object({
  start: z.string(),
  end: z.string(),
  stages: z.array(FunnelStageSchema),
  // Optional reason breakdown for cards ending in lost stage.
  lostReasons: z.array(z.object({
    reason: z.string().nullable(),
    count: z.number(),
  })),
});
export type ConversionFunnel = z.infer<typeof ConversionFunnelSchema>;

export const PostcodeRowSchema = z.object({
  outward: z.string(),                                 // e.g. "SW1A", "BR2"
  jobCount: z.number(),
  revenue: z.number(),
});
export type PostcodeRow = z.infer<typeof PostcodeRowSchema>;

export const LeadSourceRowSchema = z.object({
  source: z.string().nullable(),                       // null = "(unknown)"
  jobCount: z.number(),
  revenue: z.number(),
});
export type LeadSourceRow = z.infer<typeof LeadSourceRowSchema>;

export const RepeatCustomersSchema = z.object({
  windowDays: z.number(),
  totalWonJobs: z.number(),
  jobsFromRepeatCustomers: z.number(),
  repeatRate: z.number(),                              // fraction 0..1
  topAccounts: z.array(z.object({
    aid: z.number(),
    accountName: z.string().nullable(),
    lifetimeRevenue: z.number(),
    lifetimeJobs: z.number(),
    lastJobDate: z.string().nullable(),
  })),
});
export type RepeatCustomers = z.infer<typeof RepeatCustomersSchema>;

// ---- Phase 2: Expenses ----

export const EXPENSE_RECURRENCES = ["one_off", "weekly", "monthly", "yearly"] as const;
export type ExpenseRecurrence = (typeof EXPENSE_RECURRENCES)[number];
export const ExpenseRecurrenceSchema = z.enum(EXPENSE_RECURRENCES);

export const EXPENSE_SOURCES = ["manual", "csv"] as const;
export type ExpenseSource = (typeof EXPENSE_SOURCES)[number];
export const ExpenseSourceSchema = z.enum(EXPENSE_SOURCES);

export const ExpenseCategorySchema = z.object({
  id: z.number().int().positive(),
  name: z.string(),
  displayOrder: z.number().int(),
  archivedAt: z.string().nullable(),
});
export type ExpenseCategory = z.infer<typeof ExpenseCategorySchema>;

// POST /api/business-growth/categories body
export const CreateExpenseCategorySchema = z.object({
  name: z.string().min(1).max(60),
});
export type CreateExpenseCategory = z.infer<typeof CreateExpenseCategorySchema>;

export const ExpenseSchema = z.object({
  id: z.number().int().positive(),
  name: z.string(),
  amount: z.number(),                                  // positive £
  category: z.string(),
  recurrence: ExpenseRecurrenceSchema,
  recurrenceDay: z.number().int().min(1).max(31).nullable(),
  startsOn: z.string().nullable(),                     // YYYY-MM-DD
  endsOn: z.string().nullable(),
  transactionDate: z.string().nullable(),              // for one_off (CSV import)
  source: ExpenseSourceSchema,
  sourceFile: z.string().nullable(),
  matchedRecurringId: z.number().int().positive().nullable(),
  notes: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type Expense = z.infer<typeof ExpenseSchema>;

// POST /api/business-growth/expenses body
export const CreateExpenseSchema = z.object({
  name: z.string().min(1).max(160),
  amount: z.number().positive(),
  category: z.string().min(1).max(60),
  recurrence: ExpenseRecurrenceSchema,
  recurrenceDay: z.number().int().min(1).max(31).nullable().optional(),
  startsOn: z.string().nullable().optional(),
  endsOn: z.string().nullable().optional(),
  transactionDate: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
});
export type CreateExpense = z.infer<typeof CreateExpenseSchema>;

// PATCH /api/business-growth/expenses/:id body — same shape, all optional.
export const PatchExpenseSchema = CreateExpenseSchema.partial();
export type PatchExpense = z.infer<typeof PatchExpenseSchema>;

// CSV import — POST /api/business-growth/expenses/import-bulk body.
// Client parses the CSV with papaparse, maps columns, then sends rows.
export const ImportExpenseRowSchema = z.object({
  transactionDate: z.string(),                         // YYYY-MM-DD
  description: z.string(),
  amount: z.number().positive(),                       // already flipped to positive
  category: z.string().min(1).max(60),
  matchedRecurringId: z.number().int().positive().nullable().optional(),
});
export type ImportExpenseRow = z.infer<typeof ImportExpenseRowSchema>;

export const ImportExpensesSchema = z.object({
  sourceFile: z.string().min(1).max(255),
  rows: z.array(ImportExpenseRowSchema).min(1),
});
export type ImportExpenses = z.infer<typeof ImportExpensesSchema>;

export const ImportExpensesResultSchema = z.object({
  inserted: z.number().int(),
  matchedToRecurring: z.number().int(),
  duplicatesSkipped: z.number().int(),
});
export type ImportExpensesResult = z.infer<typeof ImportExpensesResultSchema>;

// GET /api/business-growth/monthly-spend?month=YYYY-MM
export const MonthlySpendCategorySchema = z.object({
  category: z.string(),
  amount: z.number(),
  manualRecurring: z.number(),                         // sum of recurring templates active in month
  oneOff: z.number(),                                  // sum of CSV rows in month NOT matched to a recurring template
});
export type MonthlySpendCategory = z.infer<typeof MonthlySpendCategorySchema>;

export const MonthlySpendSchema = z.object({
  month: z.string(),                                   // YYYY-MM
  total: z.number(),
  manualRecurringTotal: z.number(),
  oneOffTotal: z.number(),
  byCategory: z.array(MonthlySpendCategorySchema),
});
export type MonthlySpend = z.infer<typeof MonthlySpendSchema>;

// ---- Phase 3: P&L ----

export const PnlPointSchema = z.object({
  month: z.string(),                                   // YYYY-MM
  revenue: z.number(),
  expenses: z.number(),                                // includes wages from B_TCLEANER_STATEMENT
  wagesPart: z.number(),                               // subset of expenses (cleaner pay)
  profit: z.number(),                                  // revenue - expenses
  marginPct: z.number().nullable(),                    // profit / revenue × 100, null when revenue == 0
});
export type PnlPoint = z.infer<typeof PnlPointSchema>;

export const PnlSummarySchema = z.object({
  months: z.array(PnlPointSchema),
  ytd: z.object({
    revenue: z.number(),
    expenses: z.number(),
    profit: z.number(),
    marginPct: z.number().nullable(),
  }),
});
export type PnlSummary = z.infer<typeof PnlSummarySchema>;

// ---- Long-term performance (multi-year) -------------------------------------
// Drives the "How does today compare to peak" view for the Monday Google
// Ads strategy meeting. Combines revenue + jobs + active-tech count from
// B_TVISIT with ad spend + CPC from B_TADS_DAILY.

export const LongTermMonthSchema = z.object({
  month: z.string(),                                   // YYYY-MM
  revenue: z.number(),
  jobCount: z.number().int(),
  avgJobValue: z.number(),
  activeTechCount: z.number().int(),                   // distinct techs with ≥1 completed visit that month
  // Google Ads metrics — null if no ad data exists for that month
  // (account paused, never ran, or pre-sync).
  adSpend: z.number().nullable(),                      // £
  adImpressions: z.number().nullable(),
  adClicks: z.number().nullable(),
  avgCpc: z.number().nullable(),                       // £/click
});
export type LongTermMonth = z.infer<typeof LongTermMonthSchema>;

export const LongTermBestMonthSchema = z.object({
  month: z.string(),
  revenue: z.number(),
  jobCount: z.number().int(),
  activeTechCount: z.number().int(),
});
export type LongTermBestMonth = z.infer<typeof LongTermBestMonthSchema>;

export const LongTermSummarySchema = z.object({
  months: z.array(LongTermMonthSchema),
  // Highlights for the "what does it take to get back" framing.
  bestRevenueMonth: LongTermBestMonthSchema.nullable(),
  bestJobCountMonth: LongTermBestMonthSchema.nullable(),
  peakTechCount: z.number().int(),
  peakTechMonth: z.string().nullable(),
  // Year-over-year stat tiles (current vs prior year totals).
  yoy: z.object({
    revenueCurrent: z.number(),
    revenuePrior: z.number(),
    jobsCurrent: z.number().int(),
    jobsPrior: z.number().int(),
    techsCurrent: z.number().int(),
    techsPrior: z.number().int(),
  }),
});
export type LongTermSummary = z.infer<typeof LongTermSummarySchema>;

// ---- Phase B widgets (bank-data-driven) -------------------------------------

export const TopPayeeRowSchema = z.object({
  payee: z.string(),
  category: z.string(),
  total: z.number(),
  txnCount: z.number().int(),
  firstSeen: z.string(),
  lastSeen: z.string(),
  // Year-over-year comparison
  ytdCurrent: z.number(),
  ytdPrior: z.number(),
});
export type TopPayeeRow = z.infer<typeof TopPayeeRowSchema>;

export const SpendCategoryMonthSchema = z.object({
  month: z.string(),                                   // YYYY-MM
  byCategory: z.record(z.string(), z.number()),        // category → £
  total: z.number(),
});
export type SpendCategoryMonth = z.infer<typeof SpendCategoryMonthSchema>;

export const SpendTrendSchema = z.object({
  months: z.array(SpendCategoryMonthSchema),
  categories: z.array(z.string()),                     // sorted desc by total
});
export type SpendTrend = z.infer<typeof SpendTrendSchema>;

export const BankReconciliationMonthSchema = z.object({
  month: z.string(),                                   // YYYY-MM
  bankIncome: z.number(),                              // sum B_TBANK_INCOME
  invoicedRevenue: z.number(),                         // sum B_TVISIT.TOTALPAYMENTMADE
  difference: z.number(),                              // bank - invoiced
  deltaPct: z.number().nullable(),                     // null if invoiced == 0
  flagged: z.boolean(),                                // true when |deltaPct| > 5
});
export type BankReconciliationMonth = z.infer<typeof BankReconciliationMonthSchema>;

export const BankReconciliationSchema = z.object({
  months: z.array(BankReconciliationMonthSchema),
});
export type BankReconciliation = z.infer<typeof BankReconciliationSchema>;

export const CleanerPayAuditRowSchema = z.object({
  technicianId: z.number().int(),
  nickname: z.string().nullable(),
  month: z.string(),                                   // YYYY-MM
  bankPaid: z.number(),                                // sum B_TBANK_CLEANER_PAY
  statementPaid: z.number(),                           // sum B_TCLEANER_STATEMENT.payment_amount
  difference: z.number(),                              // bank - statement
  flagged: z.boolean(),                                // |difference| > £5
});
export type CleanerPayAuditRow = z.infer<typeof CleanerPayAuditRowSchema>;

export const CleanerPayAuditSchema = z.object({
  rows: z.array(CleanerPayAuditRowSchema),
});
export type CleanerPayAudit = z.infer<typeof CleanerPayAuditSchema>;

// ---- Phase C — Growth insight (Claude-generated commentary) -----------------

export const GrowthInsightSchema = z.object({
  id: z.number().int().positive(),
  generatedAt: z.string(),
  generatedBy: z.string().nullable(),
  model: z.string(),
  headline: z.string().nullable(),
  bodyMarkdown: z.string(),
});
export type GrowthInsight = z.infer<typeof GrowthInsightSchema>;

// One concrete next-action parsed from the latest Claude-generated insight.
// Drives the "This week's focus" tile at the top of Business Growth — staff
// tick the box, the row gets done_at/done_by stamped.
export const InsightActionSchema = z.object({
  id: z.number().int().positive(),
  insightId: z.number().int().positive(),
  ordinal: z.number().int(),
  title: z.string(),
  metric: z.string().nullable(),
  estimatedImpact: z.string().nullable(),
  doneAt: z.string().nullable(),
  doneBy: z.string().nullable(),
  notes: z.string().nullable(),
});
export type InsightAction = z.infer<typeof InsightActionSchema>;

export const PatchInsightActionSchema = z.object({
  done: z.boolean(),
  notes: z.string().nullable().optional(),
});
export type PatchInsightAction = z.infer<typeof PatchInsightActionSchema>;

// ---- DB board (Darren's private subscription tracker) ----------------------

export const DB_SUBSCRIPTION_STATUSES = [
  "untouched",
  "investigating",
  "cancelled",
  "keep",
  "renegotiated",
] as const;
export type DbSubscriptionStatus = (typeof DB_SUBSCRIPTION_STATUSES)[number];
export const DbSubscriptionStatusSchema = z.enum(DB_SUBSCRIPTION_STATUSES);

export const DbSubscriptionSchema = z.object({
  id: z.number().int().positive(),
  payee: z.string(),
  category: z.string().nullable(),
  txnCount: z.number().int(),
  monthsSeen: z.number().int(),
  totalPaid: z.number(),
  avgPerMonth: z.number(),
  minAmount: z.number().nullable(),
  maxAmount: z.number().nullable(),
  firstSeen: z.string().nullable(),
  lastSeen: z.string().nullable(),
  status: DbSubscriptionStatusSchema,
  notes: z.string().nullable(),
  source: z.string(),
  updatedBy: z.string().nullable(),
  importedAt: z.string(),
  updatedAt: z.string(),
});
export type DbSubscription = z.infer<typeof DbSubscriptionSchema>;

export const PatchDbSubscriptionSchema = z.object({
  status: DbSubscriptionStatusSchema.optional(),
  notes: z.string().nullable().optional(),
});
export type PatchDbSubscription = z.infer<typeof PatchDbSubscriptionSchema>;

// ===========================================================================
// Marketing Dashboard
// ===========================================================================
// Phase 1 (read-only): outreach funnel from campaign_outreach_log, channel
// mix from message table, day-of-week heatmap, postcode revenue, lead-source
// attribution. Phase 2 (writeable): B_TCAMPAIGN + Gantt timeline + ROI.
//
// Auth-gated by EARNINGS_ADMIN_EMAILS (same allowlist as cleaner-earnings
// and business-growth — commercial data).

// ---- Phase 1: Headline + funnels --------------------------------------------

// Reuses HeadlineMetricSchema from Business Growth section above.

export const MarketingHeadlineSchema = z.object({
  month: z.string(),                                   // YYYY-MM
  outboundSends: HeadlineMetricSchema,                 // total outbound across all channels
  replyRate: HeadlineMetricSchema,                     // fraction 0..1 — replies-within-7d / sends
  bookingsAttributed: HeadlineMetricSchema,            // distinct cards where outbound preceded won
  revenueAttributed: HeadlineMetricSchema,             // £ on those won jobs
});
export type MarketingHeadline = z.infer<typeof MarketingHeadlineSchema>;

// One funnel row per campaign_name in campaign_outreach_log. "booked" =
// account_aid linked to a B_TVISIT that reached "Booking made (Converted)"
// after the send. "responded" = responded_at IS NOT NULL.
export const OutboundFunnelRowSchema = z.object({
  campaignName: z.string(),
  channel: z.string(),                                 // 'whatsapp' | 'email' | 'sms' (mixed allowed)
  sends: z.number().int(),
  replied: z.number().int(),
  booked: z.number().int(),
  revenue: z.number(),
  firstSend: z.string().nullable(),                    // ISO
  lastSend: z.string().nullable(),
});
export type OutboundFunnelRow = z.infer<typeof OutboundFunnelRowSchema>;

export const OutboundFunnelSchema = z.object({
  start: z.string(),                                   // YYYY-MM-DD
  end: z.string(),
  rows: z.array(OutboundFunnelRowSchema),
});
export type OutboundFunnel = z.infer<typeof OutboundFunnelSchema>;

// Channel mix donut. One row per (channel, direction) combo in last N days.
export const ChannelMixRowSchema = z.object({
  channel: ChannelSchema,
  direction: z.enum(["in", "out"]),
  count: z.number().int(),
  // How many of the outbound were AI-authored (emma_auto + emma_draft).
  // Always 0 for inbound rows.
  agentCount: z.number().int(),
});
export type ChannelMixRow = z.infer<typeof ChannelMixRowSchema>;

export const ChannelMixSchema = z.object({
  windowDays: z.number().int(),
  rows: z.array(ChannelMixRowSchema),
});
export type ChannelMix = z.infer<typeof ChannelMixSchema>;

// 7-day × 24-hour heatmap of outbound→reply rate. dayOfWeek 1=Mon .. 7=Sun
// to match MySQL DAYOFWEEK convention shifted (so Mon-first reads naturally).
// Backend computes: for each outbound message in window, did the same card
// get an inbound within 7 days? Cell value = repliedSent / sent.
export const HeatmapCellSchema = z.object({
  dayOfWeek: z.number().int().min(1).max(7),
  hour: z.number().int().min(0).max(23),
  sends: z.number().int(),
  replied: z.number().int(),
  // null when sends == 0 — UI renders as a blank cell rather than 0%.
  replyRate: z.number().nullable(),
});
export type HeatmapCell = z.infer<typeof HeatmapCellSchema>;

export const DayOfWeekHeatmapSchema = z.object({
  windowDays: z.number().int(),
  cells: z.array(HeatmapCellSchema),
});
export type DayOfWeekHeatmap = z.infer<typeof DayOfWeekHeatmapSchema>;

// Lead-source attribution. For each won card in window, attributes revenue
// to the source that touched it before the win:
//   "outbound"  — at least one campaign_outreach_log row before stage=won
//   "google_ads" — phone match in Conversion Tracker (placeholder until
//                  Phase 3; emits 0 for now and the UI labels it accordingly)
//   "inbound"   — everything else (organic / direct / referral)
export const LEAD_SOURCES = ["outbound", "google_ads", "inbound"] as const;
export type LeadSourceLabel = (typeof LEAD_SOURCES)[number];
export const LeadSourceLabelSchema = z.enum(LEAD_SOURCES);

export const LeadSourceMonthSchema = z.object({
  month: z.string(),                                   // YYYY-MM
  outbound: z.object({ jobs: z.number().int(), revenue: z.number() }),
  googleAds: z.object({ jobs: z.number().int(), revenue: z.number() }),
  inbound: z.object({ jobs: z.number().int(), revenue: z.number() }),
});
export type LeadSourceMonth = z.infer<typeof LeadSourceMonthSchema>;

export const LeadSourceAttributionSchema = z.object({
  start: z.string(),                                   // YYYY-MM-DD
  end: z.string(),
  months: z.array(LeadSourceMonthSchema),
});
export type LeadSourceAttribution = z.infer<typeof LeadSourceAttributionSchema>;

// Recent outreach log rows for the "what's been sent lately" panel.
export const OutreachLogRowSchema = z.object({
  id: z.number().int(),
  campaignName: z.string(),
  channel: z.string(),
  accountAid: z.number().int().nullable(),
  contactCid: z.number().int().nullable(),
  contactName: z.string().nullable(),
  bodyPreview: z.string(),
  status: z.string(),
  sentAt: z.string().nullable(),
  respondedAt: z.string().nullable(),
  cardId: z.number().int().nullable(),
});
export type OutreachLogRow = z.infer<typeof OutreachLogRowSchema>;

export const OutreachLogResponseSchema = z.object({
  rows: z.array(OutreachLogRowSchema),
});
export type OutreachLogResponse = z.infer<typeof OutreachLogResponseSchema>;

// ---- Phase 2: Campaigns -----------------------------------------------------

export const CAMPAIGN_STATUSES = [
  "planned",
  "running",
  "paused",
  "completed",
  "cancelled",
] as const;
export type CampaignStatus = (typeof CAMPAIGN_STATUSES)[number];
export const CampaignStatusSchema = z.enum(CAMPAIGN_STATUSES);

export const CAMPAIGN_CHANNELS = [
  "email",
  "sms",
  "whatsapp",
  "voip",
  "google_ads",
  "bing_ads",
  "meta_ads",
  "referral",
  "organic",
  "mixed",
] as const;
export type CampaignChannel = (typeof CAMPAIGN_CHANNELS)[number];
export const CampaignChannelSchema = z.enum(CAMPAIGN_CHANNELS);

export const CampaignSchema = z.object({
  id: z.number().int().positive(),
  name: z.string(),
  status: CampaignStatusSchema,
  channel: CampaignChannelSchema,
  startsOn: z.string().nullable(),                     // YYYY-MM-DD
  endsOn: z.string().nullable(),
  audienceSize: z.number().int().nullable(),
  budgetGbp: z.number().nullable(),
  actualCostGbp: z.number().nullable(),
  // Joins to campaign_outreach_log.campaign_name. When set, the detail
  // panel auto-fills with sends/replied/booked/revenue from the funnel.
  outreachKey: z.string().nullable(),
  notes: z.string().nullable(),
  hypothesis: z.string().nullable(),
  retroSummary: z.string().nullable(),
  createdBy: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  // Derived rollup from campaign_outreach_log when outreachKey is set.
  // Null when there's no linked outreach data.
  rollup: z.object({
    sends: z.number().int(),
    replied: z.number().int(),
    booked: z.number().int(),
    revenue: z.number(),
  }).nullable(),
});
export type Campaign = z.infer<typeof CampaignSchema>;

export const CreateCampaignSchema = z.object({
  name: z.string().min(1).max(120),
  status: CampaignStatusSchema.default("planned"),
  channel: CampaignChannelSchema,
  startsOn: z.string().nullable().optional(),
  endsOn: z.string().nullable().optional(),
  audienceSize: z.number().int().nonnegative().nullable().optional(),
  budgetGbp: z.number().nonnegative().nullable().optional(),
  actualCostGbp: z.number().nonnegative().nullable().optional(),
  outreachKey: z.string().max(120).nullable().optional(),
  notes: z.string().nullable().optional(),
  hypothesis: z.string().nullable().optional(),
  retroSummary: z.string().nullable().optional(),
});
export type CreateCampaign = z.infer<typeof CreateCampaignSchema>;

export const PatchCampaignSchema = CreateCampaignSchema.partial();
export type PatchCampaign = z.infer<typeof PatchCampaignSchema>;

// ---- Local Services Ads (LSA) integration -----------------------------------
// Daily-pull data from Google's Local Services API. One row per lead in
// B_TLSA_LEAD; the dashboard summary rolls up by date range on the fly.

export const LSA_LEAD_TYPES = ["PHONE_CALL", "MESSAGE", "BOOKING", "UNKNOWN"] as const;
export type LsaLeadType = (typeof LSA_LEAD_TYPES)[number];
export const LsaLeadTypeSchema = z.enum(LSA_LEAD_TYPES);

export const LsaSummarySchema = z.object({
  windowDays: z.number().int(),
  leads: z.number().int(),
  chargedLeads: z.number().int(),
  phoneLeads: z.number().int(),
  messageLeads: z.number().int(),
  bookingLeads: z.number().int(),
  disputed: z.number().int(),
  spendGbp: z.number(),
  // Cost per charged lead in £. Null when zero charged leads — UI shows "—".
  costPerChargedLead: z.number().nullable(),
  lastSyncedAt: z.string().nullable(),
});
export type LsaSummary = z.infer<typeof LsaSummarySchema>;

export const LsaLeadSchema = z.object({
  leadId: z.string(),
  customerId: z.string(),
  businessName: z.string().nullable(),
  leadType: LsaLeadTypeSchema,
  leadCategory: z.string().nullable(),
  leadCreationAt: z.string(),
  chargeStatus: z.string().nullable(),
  disputeStatus: z.string().nullable(),
  chargeAmountGbp: z.number().nullable(),
  currencyCode: z.string().nullable(),
  consumerPhone: z.string().nullable(),
  consumerFirstName: z.string().nullable(),
  consumerLastName: z.string().nullable(),
  messageText: z.string().nullable(),
  callDurationSeconds: z.number().int().nullable(),
  geo: z.string().nullable(),
  timezoneId: z.string().nullable(),
  // Phase 2 — phone-match against existing kanban + booked visits.
  matchedCardId: z.number().int().nullable(),
  matchedVisitVid: z.number().int().nullable(),
  updatedAt: z.string(),
});
export type LsaLead = z.infer<typeof LsaLeadSchema>;

export const LsaSyncResultSchema = z.object({
  pulled: z.number().int(),
  upserted: z.number().int(),
  start: z.string(),
  end: z.string(),
  customerId: z.string(),
});
export type LsaSyncResult = z.infer<typeof LsaSyncResultSchema>;

// ===========================================================================
// Tech Coverage Dashboard
// ===========================================================================
// Surfaces who's active, where they cover, how busy they are, and silently
// flags techs who haven't worked in N days (default 14d) so multi-week
// holidays / sickness don't go unnoticed.
//
// Data sources: B_TUSERS (active filter), B_TVISIT (jobs), the dispatch
// FastAPI service (tech home_postcode), B_TTECH_STATE (idle-state cache so
// we can detect *changes* and push SSE alerts only when state flips).

export const TechLastJobSchema = z.object({
  date: z.string(),                                  // YYYY-MM-DD
  postcode: z.string().nullable(),
  postcodeOutward: z.string().nullable(),
  jobValue: z.number(),
});
export type TechLastJob = z.infer<typeof TechLastJobSchema>;

export const IdleAlertSchema = z.object({
  triggered: z.boolean(),
  // Calendar days since the most recent completed visit. Null when the
  // tech has never had a completed job (brand new tech).
  sinceDays: z.number().int().nullable(),
  // Calendar days until the next booked visit. Null when nothing is
  // booked in the lookahead window.
  daysToNextBooking: z.number().int().nullable(),
});
export type IdleAlert = z.infer<typeof IdleAlertSchema>;

// One scheduled time-off window per tech. Multiple rows allowed per tech
// (e.g. holiday in June + dentist in July). Drives capacity calc and the
// "show planned absences on the dashboard" widget.
export const TechUnavailabilitySchema = z.object({
  id: z.number().int().positive(),
  technicianId: z.number().int().positive(),
  startsOn: z.string(),                              // YYYY-MM-DD
  endsOn: z.string(),                                // YYYY-MM-DD inclusive
  reason: z.string().nullable(),
  source: z.enum(["manual", "tech_reply", "auto_inferred", "tech_app"]),
  confirmedAt: z.string().nullable(),
  createdBy: z.string().nullable(),
  notes: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
});
export type TechUnavailability = z.infer<typeof TechUnavailabilitySchema>;

export const CreateUnavailabilitySchema = z.object({
  technicianId: z.number().int().positive(),
  startsOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  endsOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  reason: z.string().max(200).nullable().optional(),
  notes: z.string().nullable().optional(),
});
export type CreateUnavailability = z.infer<typeof CreateUnavailabilitySchema>;

export const TechSummarySchema = z.object({
  technicianId: z.number().int().positive(),
  nickname: z.string(),
  // Pulled from the FastAPI scheduling service (/api/dispatch/board).
  // Null if the tech doesn't have a home postcode set in that service yet.
  homePostcode: z.string().nullable(),
  // Comma-split B_TUSERS.LOCATIONS_COVERED. Empty array = no claimed coverage.
  locationsCovered: z.array(z.string()),
  // Same postcodes as locationsCovered, grouped by county (via uk_postcodes_dev —
  // the same lookup the tech-portal Coverage picker uses). Counties sorted asc;
  // postcodes within each county sorted asc. Postcodes whose county can't be
  // resolved go in the "Unknown" bucket. Empty when locationsCovered is empty.
  coverageByCounty: z.array(z.object({
    county: z.string(),
    postcodes: z.array(z.string()),
  })),
  // Most recent completed job. Null = brand-new tech with no work yet.
  lastCompletedJob: TechLastJobSchema.nullable(),
  daysSinceLastJob: z.number().int().nullable(),
  upcomingVisits: z.number().int(),                  // count next 14d
  upcomingValue: z.number(),                         // £ next 14d
  // Capacity = sum of SCHEDULEHOURS / (8h × workdays in window). Clamp 0..1+.
  capacityPctNext14d: z.number(),
  // Average jobs/day over the last 90d (only days with completed work
  // counted in the denominator) — drives "weekly capacity" estimate.
  avgJobsPerWorkingDay: z.number(),
  // Working days the tech is unavailable in next 30d (sum of overlap
  // between Mon-Fri days and any active unavailability rows).
  unavailableWorkingDaysNext30: z.number().int(),
  // Active + upcoming unavailability rows that affect the next 60 days.
  upcomingUnavailability: z.array(TechUnavailabilitySchema),
  idleAlert: IdleAlertSchema,
});
export type TechSummary = z.infer<typeof TechSummarySchema>;

export const TechCoverageSummarySchema = z.object({
  thresholdDays: z.number().int(),
  techs: z.array(TechSummarySchema),
  generatedAt: z.string(),
});
export type TechCoverageSummary = z.infer<typeof TechCoverageSummarySchema>;

// POST /api/coverage/evaluate-idle response — n8n cron + manual button hit this.
export const IdleEvaluationResultSchema = z.object({
  thresholdDays: z.number().int(),
  evaluated: z.number().int(),
  changed: z.array(z.object({
    technicianId: z.number().int(),
    nickname: z.string(),
    isIdle: z.boolean(),
    sinceDays: z.number().int().nullable(),
  })),
});
export type IdleEvaluationResult = z.infer<typeof IdleEvaluationResultSchema>;

// Per-outward map data for the Tech Coverage map. Combines
// B_TPOSTCODE_GEO (lat/lon) with last-90d B_TVISIT activity and the
// list of technicians whose LOCATIONS_COVERED includes this outward.
export const CoveragePostcodeSchema = z.object({
  outward: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  region: z.string().nullable(),
  jobCount: z.number().int(),                        // last 90d, completed
  revenue: z.number(),                               // last 90d, £
  // Numeric IDs of every active tech with this outward in LOCATIONS_COVERED.
  // Empty array = uncovered (we get jobs there but no tech claims it).
  techIds: z.array(z.number().int()),
});
export type CoveragePostcode = z.infer<typeof CoveragePostcodeSchema>;

export const CoverageMapResponseSchema = z.object({
  windowDays: z.number().int(),
  postcodes: z.array(CoveragePostcodeSchema),
  // Reuse TechSummary so the frontend can colour markers per-tech using
  // the same nickname/colour pairing that drives the Phase-1 cards.
  techs: z.array(z.object({
    technicianId: z.number().int().positive(),
    nickname: z.string(),
  })),
});
export type CoverageMapResponse = z.infer<typeof CoverageMapResponseSchema>;

// ===========================================================================
// Area Analysis Dashboard
// ===========================================================================
// Strategic map view of UK postcodes — wealth × revenue × coverage. Helps
// answer "where should we recruit / advertise / push outbound". Phase 5+
// will layer competitor density and CPC, but Phase 4 ships the read-only
// wealth + revenue picture so we can get value from manual tagging today.

export const POSTCODE_BANDS = ["premium", "mid", "value", "unknown"] as const;
export type PostcodeBand = (typeof POSTCODE_BANDS)[number];
export const PostcodeBandSchema = z.enum(POSTCODE_BANDS);

export const POSTCODE_BAND_SOURCES = ["imd_seed", "manual_override"] as const;
export type PostcodeBandSource = (typeof POSTCODE_BAND_SOURCES)[number];
export const PostcodeBandSourceSchema = z.enum(POSTCODE_BAND_SOURCES);

export const AreaPostcodeSchema = z.object({
  outward: z.string(),
  latitude: z.number(),
  longitude: z.number(),
  region: z.string().nullable(),
  imdScore: z.number().nullable(),
  imdDecile: z.number().int().nullable(),
  band: PostcodeBandSchema,
  bandSource: PostcodeBandSourceSchema.nullable(),
  bandNotes: z.string().nullable(),
  bandUpdatedBy: z.string().nullable(),
  bandUpdatedAt: z.string().nullable(),
  // 12-month windows for the strategic view (longer than Coverage's 90d).
  jobs12mo: z.number().int(),
  revenue12mo: z.number(),
  lastJobAt: z.string().nullable(),
  // Whether ANY active tech currently has this outward in LOCATIONS_COVERED.
  covered: z.boolean(),
  techCount: z.number().int(),
  // Competitor count from B_TCOMPETITOR — phone-deduped across sources.
  // 0 means we either haven't scraped here yet OR there genuinely are none.
  // Phase 5+ scrapers populate this.
  competitorCount: z.number().int(),
});
export type AreaPostcode = z.infer<typeof AreaPostcodeSchema>;

export const AreaSummaryResponseSchema = z.object({
  windowDays: z.number().int(),
  postcodes: z.array(AreaPostcodeSchema),
});
export type AreaSummaryResponse = z.infer<typeof AreaSummaryResponseSchema>;

export const SetPostcodeBandSchema = z.object({
  band: PostcodeBandSchema,
  notes: z.string().nullable().optional(),
});
export type SetPostcodeBand = z.infer<typeof SetPostcodeBandSchema>;

// ---- Competitor data --------------------------------------------------------
// Pulled by scrapers + Google Places API. Schema accommodates Phase 7
// recruitment outreach via recruit_status.

// 'manual' = candidate added by hand via the Recruitment dashboard. Distinct
// from the scrape sources so we can audit where each lead came from.
export const COMPETITOR_SOURCES = ["google_places", "yell", "bark", "manual"] as const;
export type CompetitorSource = (typeof COMPETITOR_SOURCES)[number];
export const CompetitorSourceSchema = z.enum(COMPETITOR_SOURCES);

export const COMPETITOR_RECRUIT_STATUSES = [
  "not_contacted",
  "contacted",
  "interested",
  "rejected",
  "hired",
] as const;
export type CompetitorRecruitStatus = (typeof COMPETITOR_RECRUIT_STATUSES)[number];
export const CompetitorRecruitStatusSchema = z.enum(COMPETITOR_RECRUIT_STATUSES);

export const TECHNICIAN_TYPES = [
  "previous_breeze",
  "competitor",
  "independent",
  "career_changer",
  "referral",
  "social_lead",
  "unknown",
] as const;
export type TechnicianType = (typeof TECHNICIAN_TYPES)[number];
export const TechnicianTypeSchema = z.enum(TECHNICIAN_TYPES);

export const CONTACT_METHODS = ["whatsapp", "sms", "email", "call", "unknown"] as const;
export type ContactMethod = (typeof CONTACT_METHODS)[number];
export const ContactMethodSchema = z.enum(CONTACT_METHODS);

// Line type from Twilio Lookup v2 LineTypeIntelligence. Drives whether
// SMS will work + influences WhatsApp likelihood:
//   - mobile: SMS works, WhatsApp very likely
//   - landline: no SMS; WhatsApp Business sometimes (e.g. small biz like Marcus)
//   - fixedVoip / nonFixedVoip: VoIP — SMS varies by carrier; WhatsApp possible
//   - tollFree / personal / pager: rare for our targets; no SMS expected
//   - null: not yet looked up
export const LINE_TYPES = [
  "mobile",
  "landline",
  "fixedVoip",
  "nonFixedVoip",
  "tollFree",
  "personal",
  "pager",
  "unknown",
] as const;
export type LineType = (typeof LINE_TYPES)[number];
export const LineTypeSchema = z.enum(LINE_TYPES);

export const CompetitorSchema = z.object({
  id: z.number().int().positive(),
  source: CompetitorSourceSchema,
  sourceId: z.string(),
  businessName: z.string(),
  contactName: z.string().nullable(),
  postcodeOutward: z.string().nullable(),
  fullPostcode: z.string().nullable(),
  addressText: z.string().nullable(),
  latitude: z.number().nullable(),
  longitude: z.number().nullable(),
  phone: z.string().nullable(),
  phoneNormalised: z.string().nullable(),
  // Twilio Lookup v2 line_type_intelligence.type result. null until
  // checked. Drives SMS-capability badges + WhatsApp/SMS routing.
  lineType: z.string().nullable(),
  phoneIntelCheckedAt: z.string().nullable(),
  email: z.string().nullable(),
  website: z.string().nullable(),
  socialUrl: z.string().nullable(),
  rating: z.number().nullable(),
  reviewCount: z.number().int().nullable(),
  services: z.string().nullable(),
  recruitStatus: CompetitorRecruitStatusSchema,
  recruitNotes: z.string().nullable(),
  technicianType: TechnicianTypeSchema,
  preferredContactMethod: ContactMethodSchema,
  fitScore: z.number().int().min(0).max(100).nullable(),
  lastContactedAt: z.string().nullable(),
  nextFollowUpAt: z.string().nullable(),
  addedBy: z.string().nullable(),
  scrapedAt: z.string(),
  firstSeenAt: z.string(),
  updatedAt: z.string(),
});
export type Competitor = z.infer<typeof CompetitorSchema>;

// Upsert payload — same shape per row regardless of source. Scrapers / API
// callers fill what they have; missing fields default to null. UNIQUE key
// on (source, source_id) means re-runs refresh data without duplicating.
export const CompetitorUpsertRowSchema = z.object({
  source: CompetitorSourceSchema,
  sourceId: z.string().min(1).max(160),
  businessName: z.string().min(1).max(200),
  postcodeOutward: z.string().max(8).nullable().optional(),
  fullPostcode: z.string().max(12).nullable().optional(),
  addressText: z.string().max(255).nullable().optional(),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  phone: z.string().max(40).nullable().optional(),
  email: z.string().max(160).nullable().optional(),
  website: z.string().max(255).nullable().optional(),
  rating: z.number().min(0).max(5).nullable().optional(),
  reviewCount: z.number().int().nonnegative().nullable().optional(),
  services: z.string().nullable().optional(),
  rawJson: z.unknown().optional(),
});
export type CompetitorUpsertRow = z.infer<typeof CompetitorUpsertRowSchema>;

export const CompetitorUpsertBatchSchema = z.object({
  source: CompetitorSourceSchema,
  rows: z.array(CompetitorUpsertRowSchema).min(1).max(500),
});
export type CompetitorUpsertBatch = z.infer<typeof CompetitorUpsertBatchSchema>;

export const CompetitorUpsertResultSchema = z.object({
  inserted: z.number().int(),
  updated: z.number().int(),
  source: CompetitorSourceSchema,
});
export type CompetitorUpsertResult = z.infer<typeof CompetitorUpsertResultSchema>;

// Trigger Places API fetch for a single outward — kanban API runs the
// call server-side so we keep tight cost control on the paid endpoint.
export const PlacesFetchSchema = z.object({
  outward: z.string().min(2).max(8),
  // Optional radius around the outward centre. Defaults to 3000m which is
  // about right for UK outwards (most are 2-5km radius).
  radiusMeters: z.number().int().min(500).max(20000).optional(),
});
export type PlacesFetch = z.infer<typeof PlacesFetchSchema>;

// Phase 7 recruitment workflow — update a competitor row's recruit
// status / notes / recruitment metadata. All fields optional so the UI
// can patch one thing at a time without re-sending the rest.
//
// `lastContactedAt` accepts the literal "now" to stamp the current time
// server-side. The "Mark as contacted" button uses this so we don't have
// to trust client clocks.
export const PatchCompetitorSchema = z.object({
  recruitStatus: CompetitorRecruitStatusSchema.optional(),
  recruitNotes: z.string().nullable().optional(),
  contactName: z.string().max(160).nullable().optional(),
  email: z.string().max(160).nullable().optional(),
  phone: z.string().max(40).nullable().optional(),
  website: z.string().max(255).nullable().optional(),
  socialUrl: z.string().max(255).nullable().optional(),
  technicianType: TechnicianTypeSchema.optional(),
  preferredContactMethod: ContactMethodSchema.optional(),
  fitScore: z.number().int().min(0).max(100).nullable().optional(),
  lastContactedAt: z.union([z.literal("now"), z.string().datetime(), z.null()]).optional(),
  nextFollowUpAt: z.union([z.string().datetime(), z.null()]).optional(),
});
export type PatchCompetitor = z.infer<typeof PatchCompetitorSchema>;

// Manual-add payload for the Recruitment dashboard. Inserts a new row
// into B_TCOMPETITOR with source='manual'. Only `businessName` is
// strictly required — Andzelika or Darren can fill in the rest later.
export const AddRecruitCandidateSchema = z.object({
  businessName: z.string().min(1).max(200),
  contactName: z.string().max(160).nullable().optional(),
  postcodeOutward: z.string().max(8).nullable().optional(),
  phone: z.string().max(40).nullable().optional(),
  email: z.string().max(160).nullable().optional(),
  website: z.string().max(255).nullable().optional(),
  socialUrl: z.string().max(255).nullable().optional(),
  technicianType: TechnicianTypeSchema.optional(),
  preferredContactMethod: ContactMethodSchema.optional(),
  recruitNotes: z.string().nullable().optional(),
  fitScore: z.number().int().min(0).max(100).nullable().optional(),
});
export type AddRecruitCandidate = z.infer<typeof AddRecruitCandidateSchema>;

// Stripe Connect Express — same-day pay for technicians. Deliberately
// minimal: Stripe is the source of truth for KYC, banking, balance. We
// only surface an account ID + 3 webhook-derived status flags.
export const STRIPE_CONNECT_STATUSES = [
  "not_started",
  "onboarding",
  "active",
  "restricted",
] as const;
export type StripeConnectStatus = (typeof STRIPE_CONNECT_STATUSES)[number];
export const StripeConnectStatusSchema = z.enum(STRIPE_CONNECT_STATUSES);

export const StripeStatusViewSchema = z.object({
  technicianId: z.number().int().positive(),
  accountId: z.string().nullable(),
  status: StripeConnectStatusSchema,
  chargesEnabled: z.boolean(),
  payoutsEnabled: z.boolean(),
  displayLabel: z.string(),
});
export type StripeStatusView = z.infer<typeof StripeStatusViewSchema>;

export const StripeOnboardingLinkSchema = z.object({
  url: z.string().url(),
  expiresAt: z.string(),
  accountId: z.string(),
  status: StripeConnectStatusSchema,
});
export type StripeOnboardingLink = z.infer<typeof StripeOnboardingLinkSchema>;

// Strategy progress — runway-focused goal-vs-actual tracking surfaced on
// the Business Growth dashboard. Includes VAT modelling (UK threshold +
// flat rate), monthly P&L breakdown, projection for future months.
export const StrategyMonthSchema = z.object({
  month: z.string(),               // YYYY-MM
  isFuture: z.boolean(),
  isProjection: z.boolean(),
  isPreGoal: z.boolean(),

  techs: z.number(),
  jobs: z.number(),

  grossSales: z.number(),
  vatRegistered: z.boolean(),
  vatDue: z.number(),
  netSales: z.number(),
  rolling12Sales: z.number(),

  cleanerPay: z.number(),
  otherExpenses: z.number(),

  profit: z.number(),

  targetRevenue: z.number().nullable(),
  targetProfit: z.number().nullable(),
});
export type StrategyMonth = z.infer<typeof StrategyMonthSchema>;

export const StrategyProgressSchema = z.object({
  goal: z.object({
    startDate: z.string(),
    endDate: z.string(),
    targetRevenue: z.number(),
    targetTechs: z.number(),
    targetHoursPerWeek: z.number(),
    baselineRevenue: z.number(),
    runwayTargetRevenue: z.number(),
    runwayTargetTechs: z.number(),
    householdMonthlyNeed: z.number(),
    mimecastReplacementNet: z.number(),
    ownerExtractionTaxRate: z.number(),
    vatThreshold: z.number(),
    vatFlatRate: z.number(),
  }),
  months: z.array(StrategyMonthSchema),
  totals: z.object({
    actualCumulativeRevenue: z.number(),
    actualCumulativeProfit: z.number(),
    actualMonthlyProfitAvg: z.number(),
    projectedAnnualProfit: z.number(),
    profitMargin: z.number(),
    monthsAboveHouseholdNeed: z.number(),
    monthsAboveMimecastLine: z.number(),
    vatTriggerMonth: z.string().nullable(),
  }),
  techs: z.object({
    activeNow: z.number(),
    target: z.number(),
  }),
});
export type StrategyProgress = z.infer<typeof StrategyProgressSchema>;
